<template>
  <!-- Proxies page for ProProxyUI servers: one port = HTTP + SOCKS5, bound to one modem -->
  <div class="pu">
    <div class="pu-table">
      <div class="pu-row pu-row--head">
        <span></span><span>Server</span><span>Modem</span><span>Port</span><span>Login / whitelist</span><span>Public IP</span><span>Status</span><span>Sold to</span><span>OS</span><span>Today</span><span></span>
      </div>
      <div v-for="p in rows" :key="p.uuid" class="pu-row" :class="{ 'is-off': p.active === 'inactive' }">
        <span><input type="checkbox" class="form-check-input shadow-none" :checked="selected.includes(p.uuid)" @change="toggle(p.uuid)" /></span>
        <span class="pu-two"><b>{{ p.modem }}</b><small v-if="p.active === 'inactive'">not for sale</small></span>
        <span class="pu-two"><span>{{ modemLabel(p) }}</span><small>…{{ String(p.imei).slice(-4) }}</small></span>
        <span class="pu-mono">{{ p.http_port }}</span>
        <span class="pu-two"><span class="pu-mono">{{ p.auth_user || '—' }}</span><small v-if="p.auth_ips">{{ p.auth_ips }}</small></span>
        <span class="pu-mono">{{ p.public_ip || '—' }}</span>
        <span><span class="pu-pill" :class="p.status === 'CONNECTED' ? 'is-on' : 'is-bad'">{{ p.status === 'CONNECTED' ? 'Online' : 'Offline' }}</span></span>
        <span>{{ p.usage_status === 'AVAILABLE' ? 'Free' : (p.customer_name || 'Sold') }}</span>
        <span>{{ osLabel(p.os_profile) }}</span>
        <span class="pu-mono">{{ p.usage_today_mb }} MB</span>
        <span class="text-right">
          <v-menu location="bottom end">
            <template v-slot:activator="{ props }"><v-btn v-bind="props" density="compact" size="small" class="bg-dark text-light">Actions</v-btn></template>
            <v-list density="compact">
              <v-list-item @click="openEdit(p)">Edit</v-list-item>
              <v-list-item @click="modemAction(p, 'rotate')">Rotate IP</v-list-item>
              <v-list-item @click="modemAction(p, 'reboot')">Reboot modem</v-list-item>
              <v-list-item @click="modemAction(p, 'usbreset')">USB reset</v-list-item>
              <v-list-item @click="toggleActive(p)">{{ p.active === 'inactive' ? 'Put back on sale' : 'Take off sale' }}</v-list-item>
            </v-list>
          </v-menu>
        </span>
      </div>
      <p v-if="!rows.length" class="pu-empty">No ProProxyUI ports{{ selectedServerName && selectedServerName !== 'All' ? ' on this server' : '' }} yet.</p>
    </div>

    <v-dialog v-model="dialog" max-width="560">
      <div class="pu-dlg">
        <div class="pu-dlg__head"><span>{{ form.uuid ? `Edit port ${form.listen_port}` : `Add port · ${selectedServerName}` }}</span><button type="button" aria-label="Close" @click="dialog = false">&times;</button></div>
        <div class="pu-dlg__body">
          <p v-if="boxError" class="pu-err">{{ boxError }}</p>
          <div><label>Modem</label>
            <v-select v-model="form.imei" :items="modemItems" item-title="label" item-value="imei" variant="outlined" density="compact" hide-details rounded="0" :loading="boxLoading"></v-select></div>
          <p v-if="sharedWarning" class="pu-warn">This modem already has {{ sharedWarning }} port{{ sharedWarning === 1 ? '' : 's' }}. Customers on the same modem share its IP and rotations.</p>
          <div class="grid grid-cols-2 gap-3">
            <div><label>Port</label><v-text-field v-model.number="form.listen_port" type="number" variant="outlined" density="compact" hide-details rounded="0" :disabled="!!form.uuid" :placeholder="nextPort ? String(nextPort) : 'next free'"></v-text-field></div>
            <div><label>Login (user:password)</label><v-text-field v-model="form.auth_user" variant="outlined" density="compact" hide-details rounded="0" placeholder="random" class="pu-mono-input"></v-text-field></div>
          </div>
          <div><label>IP whitelist</label><v-text-field v-model="form.auth_ips" variant="outlined" density="compact" hide-details rounded="0" placeholder="none"></v-text-field></div>
          <div class="grid grid-cols-3 gap-3">
            <div><label>OS fingerprint</label><v-select v-model="form.os_profile" :items="OS_ITEMS" item-title="label" item-value="value" variant="outlined" density="compact" hide-details rounded="0"></v-select></div>
            <div><label>Speed limit (Mbps)</label><v-text-field v-model.number="form.limit_mbps" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
            <div><label>Daily data (GB)</label><v-text-field v-model.number="form.daily_gb" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div><label>Max connections</label><v-text-field v-model.number="form.max_conns" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
            <div><label>Auto-rotate (min)</label><v-text-field v-model.number="form.auto_rotate_min" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = off"></v-text-field></div>
            <div><label>MTU</label><v-text-field v-model.number="form.mtu" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = auto"></v-text-field></div>
          </div>
        </div>
        <div class="pu-dlg__foot">
          <button type="button" class="pu-btn" @click="dialog = false">Cancel</button>
          <button type="button" class="pu-btn pu-btn--pri" :disabled="busy || !form.imei" @click="save">{{ busy ? 'Saving…' : form.uuid ? 'Save' : 'Create port' }}</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch, defineProps, defineExpose } from 'vue';
import { storeToRefs } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAdminProxyStore, useAlertStore } from '@/stores';
import { regionOf } from '@/helpers/region';

const props = defineProps({ selectedServerName: String, selectedSaleStatus: String, selectedConnStatus: String, selectedRegion: { type: String, default: 'all' } });
const API = `${process.env.VUE_APP_VUE_API_URL}`;
const store = useAdminProxyStore();
const { ppuiProxies } = storeToRefs(store);
const alerts = useAlertStore();
const OS_ITEMS = [
  { value: '', label: 'Native' }, { value: 'windows', label: 'Windows' }, { value: 'windows-old', label: 'Windows (old)' }, { value: 'macos', label: 'macOS' },
  { value: 'ios', label: 'iOS' }, { value: 'android', label: 'Android' }, { value: 'android-old', label: 'Android (old)' }, { value: 'linux', label: 'Linux' }, { value: 'ubuntu24', label: 'Ubuntu 24' },
];
const osLabel = (v) => (OS_ITEMS.find((o) => o.value === (v || '')) || { label: v }).label;

const rows = computed(() => (ppuiProxies.value || []).filter((p) =>
  (props.selectedRegion === 'all' || regionOf(p.modem_country) === props.selectedRegion)
  && (!props.selectedSaleStatus || props.selectedSaleStatus === 'All' || p.usage_status === props.selectedSaleStatus)
  && (!props.selectedConnStatus || props.selectedConnStatus === 'All' || (props.selectedConnStatus === 'PAUSED' ? p.active === 'inactive' : p.status === props.selectedConnStatus))));

const selected = ref([]);
const toggle = (uuid) => { selected.value = selected.value.includes(uuid) ? selected.value.filter((u) => u !== uuid) : [...selected.value, uuid]; };
const reload = () => store.loadProxies(props.selectedServerName || 'All');

// modem names from the last box read (Add / Edit dialog), falling back to the IMEI
const boxModems = ref([]);
const modemLabel = (p) => (boxModems.value.find((m) => String(m.imei) === String(p.imei)) || {}).nickname || 'Modem';

const dialog = ref(false);
const busy = ref(false);
const boxLoading = ref(false);
const boxError = ref('');
const nextPort = ref(null);
const blank = () => ({ uuid: null, imei: null, listen_port: null, auth_user: '', auth_ips: '', os_profile: '', limit_mbps: 0, daily_gb: 0, max_conns: 0, auto_rotate_min: 0, mtu: 0, modem: null });
const form = ref(blank());
const modemItems = computed(() => boxModems.value.map((m) => ({ imei: m.imei, label: `${m.nickname} · ${m.state}${m.public_ip ? ' · ' + m.public_ip : ''}${m.ports ? ` · ${m.ports} port${m.ports === 1 ? '' : 's'}` : ''}` })));
const sharedWarning = computed(() => {
  const m = boxModems.value.find((x) => String(x.imei) === String(form.value.imei));
  if (!m) return 0;
  const others = m.ports - (form.value.uuid && String(form.value.originalImei) === String(m.imei) ? 1 : 0);
  return others > 0 ? others : 0;
});
const loadBox = async (serverName) => {
  boxLoading.value = true; boxError.value = '';
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/box`, { modem_name: serverName });
    boxModems.value = r.modems || []; nextPort.value = r.next_port;
  } catch (e) { boxError.value = typeof e === 'string' ? e : 'The box did not answer'; boxModems.value = []; }
  boxLoading.value = false;
};
// modem names for the table when one server is shown
watch(() => props.selectedServerName, (name) => { if (name && name !== 'All' && !dialog.value) loadBox(name); }, { immediate: true });
const openAdd = async () => {
  if (!props.selectedServerName || props.selectedServerName === 'All') { alerts.error('Choose a ProProxyUI server first'); return; }
  form.value = blank(); dialog.value = true;
  await loadBox(props.selectedServerName);
  const free = boxModems.value.find((m) => !m.ports && m.state === 'online') || boxModems.value[0];
  if (free) form.value.imei = free.imei;
};
const openEdit = async (p) => {
  form.value = { uuid: p.uuid, imei: p.imei, originalImei: p.imei, listen_port: p.http_port, auth_user: p.auth_user || '', auth_ips: p.auth_ips || '', os_profile: p.os_profile || '',
    limit_mbps: p.limit_mbps || 0, daily_gb: p.daily_gb || 0, max_conns: p.max_conns || 0, auto_rotate_min: p.auto_rotate_min || 0, mtu: p.mtu || 0, modem: p.modem };
  dialog.value = true;
  await loadBox(p.modem);
};
const save = async () => {
  const f = form.value;
  const body = { auth_user: f.auth_user.trim(), auth_ips: f.auth_ips, os_profile: f.os_profile, limit_mbps: f.limit_mbps || 0, daily_gb: f.daily_gb || 0, max_conns: f.max_conns || 0, auto_rotate_min: f.auto_rotate_min || 0, mtu: f.mtu || 0, imei: f.imei };
  busy.value = true;
  try {
    if (f.uuid) {
      await fetchWrapper.post(`${API}/admin/proproxyui/update-port`, { uuid: f.uuid, ...body });
      alerts.success('Port saved');
    } else {
      const r = await fetchWrapper.post(`${API}/admin/proproxyui/create-port`, { modem_name: props.selectedServerName, listen_port: f.listen_port || undefined, ...body, auth_user: body.auth_user || undefined });
      alerts.success(`Port ${r.listen_port} created`);
    }
    dialog.value = false;
    await reload();
  } catch (e) { alerts.error(e); }
  busy.value = false;
};
const modemAction = async (p, action) => {
  const what = { rotate: 'Rotate the IP of', reboot: 'Reboot', usbreset: 'USB reset' }[action];
  if (!confirm(`${what} the modem behind port ${p.http_port}? Every port on that modem drops for a moment.`)) return;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/modem-action`, { uuid: p.uuid, action });
    alerts.success(action === 'rotate' && r.result ? `New IP ${r.result.newIp}${r.result.changed === false ? ' (the carrier gave the same IP)' : ''}` : 'Done');
    await reload();
  } catch (e) { alerts.error(e); }
};
const toggleActive = async (p) => {
  try { await fetchWrapper.post(`${API}/admin/proproxyui/toggle-active`, { uuid: p.uuid }); await reload(); } catch (e) { alerts.error(e); }
};
const deleteProxies = async () => {
  if (!selected.value.length) { alerts.error('Tick the ports to delete'); return; }
  if (!confirm(`Delete ${selected.value.length} port(s) from the box? Sold ports are skipped.`)) return;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/delete-ports`, { uuids: selected.value });
    alerts.success(r.message === 'success' ? 'Deleted' : r.message);
    selected.value = [];
    await reload();
  } catch (e) { alerts.error(e); }
};
defineExpose({ openAdd, deleteProxies });
</script>

<style scoped>
.pu-table { border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card); overflow-x: auto; }
.pu-row { display: grid; grid-template-columns: 28px minmax(110px, 1.1fr) minmax(110px, 1.1fr) 70px minmax(150px, 1.4fr) 120px 80px minmax(90px, 1fr) 90px 90px 90px; gap: 10px; align-items: center; padding: 8px 12px; border-bottom: 1px solid var(--ui-line); min-width: 1100px; font-size: 13px; }
.pu-row--head { color: var(--ui-dim); font-size: 12px; }
.pu-row.is-off { opacity: 0.6; }
.pu-two { display: flex; flex-direction: column; min-width: 0; }
.pu-two small { color: var(--ui-dim); font-size: 11.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pu-mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; overflow-wrap: anywhere; }
.pu-pill { display: inline-block; padding: 1px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 600; }
.pu-pill.is-on { background: var(--ui-ok-soft); color: var(--ui-ok-ink); }
.pu-pill.is-bad { background: var(--ui-warn-soft); color: var(--ui-warn-ink); }
.pu-empty { padding: 16px; color: var(--ui-dim); margin: 0; }
.pu-dlg { background: var(--ui-card); color: var(--ui-ink); border: 1px solid var(--ui-line); border-radius: 12px; }
.pu-dlg__head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid var(--ui-line); font-size: 16px; }
.pu-dlg__head button { font-size: 22px; line-height: 1; color: inherit; background: transparent; border: 0; cursor: pointer; }
.pu-dlg__body { display: flex; flex-direction: column; gap: 12px; padding: 14px 16px; }
.pu-dlg__body label { display: block; margin-bottom: 4px; color: var(--ui-dim); font-size: 12px; }
.pu-dlg__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--ui-line); }
.pu-warn { margin: 0; padding: 6px 10px; border-radius: 8px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); font-size: 12.5px; }
.pu-err { margin: 0; padding: 6px 10px; border-radius: 8px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); font-size: 12.5px; }
.pu-btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 14px; border: 1px solid var(--ui-line-strong); border-radius: 6px; font-size: 13px; color: inherit; background: transparent; cursor: pointer; }
.pu-btn:disabled { opacity: 0.5; cursor: default; }
.pu-btn--pri { background: var(--ui-pri); border-color: var(--ui-pri); color: #ffffff; }
.pu-mono-input :deep(input) { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12.5px; }
</style>
